import { storeCreditAmountFromCode, storeCreditVariantFromCode } from '../core/discountLabel';

// Loop applies Pfotenpunkte (store credit) redemption codes at the contract
// level. When a code is attached, Loop's `discountedPrice` on the line ALREADY
// reflects the reduction. These helpers derive the credit amount for display
// purposes (showing "inkl. Pfotenpunkte -€X") but do NOT subtract it from the
// price again — the price from Loop is authoritative.

type DiscountLike = {
  isActive?: boolean;
  title?: string;
};

// Sums active store-credit codes into a `variantShopifyId -> amount` map.
export const buildStoreCreditByVariant = (
  discounts: DiscountLike[] | undefined | null,
): Record<string, number> => {
  const map: Record<string, number> = {};
  if (!discounts) return map;

  for (const discount of discounts) {
    if (!discount?.isActive) continue;
    const variant = storeCreditVariantFromCode(discount.title);
    const amount = storeCreditAmountFromCode(discount.title);
    if (!variant || amount == null || amount <= 0) continue;
    map[variant] = (map[variant] ?? 0) + amount;
  }

  return map;
};

// Credit for one line, capped so it never takes the line below zero.
export const creditForLine = (
  variantShopifyId: number | string | undefined,
  lineSubtotal: number,
  creditByVariant: Record<string, number>,
): number => {
  if (variantShopifyId == null) return 0;
  const credit = creditByVariant[String(variantShopifyId)] ?? 0;
  if (credit <= 0 || lineSubtotal <= 0) return 0;
  return Math.min(credit, lineSubtotal);
};

const lineSubtotal = (item: ILineItem): number => {
  const rawUnit = item.discountedPrice ?? item.price ?? 0;
  const unit = typeof rawUnit === 'number' ? rawUnit : parseFloat(rawUnit);
  const qty = Number.isFinite(item.quantity) && item.quantity > 0 ? item.quantity : 1;
  return (Number.isFinite(unit) ? unit : 0) * qty;
};

// Total credit across items, summed per variant and capped at each variant's
// subtotal so the order total never drops below the items' cost.
export const creditForItems = (
  items: ILineItem[] | undefined | null,
  creditByVariant: Record<string, number>,
): number => {
  if (!items || items.length === 0) return 0;

  const subtotalByVariant: Record<string, number> = {};
  for (const item of items) {
    if (item?.variantShopifyId == null) continue;
    const key = String(item.variantShopifyId);
    subtotalByVariant[key] = (subtotalByVariant[key] ?? 0) + lineSubtotal(item);
  }

  return Object.entries(subtotalByVariant).reduce((total, [variant, subtotal]) => {
    const credit = creditByVariant[variant] ?? 0;
    return total + (credit > 0 ? Math.min(credit, subtotal) : 0);
  }, 0);
};
